import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import HeadersNonAuth from '../../components/base/HeadersNonAuth';

const MAX_ATTEMPTS = 5;

const TwoFactorVerifyPage = () => {
  const navigate = useNavigate();
  const { mfaResolver, verifyMfaCode, clearMfaResolver } = useAuthContext();

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockedOut, setLockedOut] = useState(false);
  const inputRefs = useRef([]);
  const isSubmittingRef = useRef(false);

  // Redirect if no MFA challenge pending (e.g. page refresh lost the resolver)
  useEffect(() => {
    if (!mfaResolver) {
      navigate('/login', { replace: true });
    }
  }, [mfaResolver, navigate]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] || '';
    }
    setCode(newCode);

    const lastIndex = Math.min(pasted.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleSubmit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Prevent double submission
    if (isSubmittingRef.current || isVerifying || lockedOut) return;

    const otpCode = code.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the full 6-digit code');
      return;
    }

    isSubmittingRef.current = true;
    setIsVerifying(true);
    setError('');

    try {
      await verifyMfaCode(otpCode);

      // Honor redirectAfterLogin if set, otherwise go to dashboard
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        // Only allow internal paths to prevent open redirect
        if (redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
          navigate(redirectUrl);
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        setLockedOut(true);
        setError(`Too many failed attempts. Please go back and try logging in again.`);
        return;
      }

      if (
        err.code === 'auth/totp-challenge-timeout' ||
        err.code === 'auth/session-expired' ||
        err.code === 'auth/invalid-multi-factor-session'
      ) {
        setError('Your session has expired. Please log in again.');
        clearMfaResolver();
        setTimeout(() => navigate('/login', { replace: true }), 2000);
        return;
      } else if (err.code === 'auth/invalid-verification-code') {
        setError(`Invalid code. ${MAX_ATTEMPTS - newAttempts} attempt(s) remaining.`);
      } else {
        setError('Verification failed. Please try again.');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
      isSubmittingRef.current = false;
    }
  }, [code, isVerifying, lockedOut, attempts, verifyMfaCode, clearMfaResolver, navigate]);

  const handleCancel = () => {
    clearMfaResolver();
    navigate('/login');
  };

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (code.every((d) => d !== '') && !isVerifying && !isSubmittingRef.current && !lockedOut) {
      handleSubmit();
    }
  }, [code, isVerifying, lockedOut, handleSubmit]);

  if (!mfaResolver) return null;

  return (
    <>
      <HeadersNonAuth />
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 pt-32 pb-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Two-Factor Authentication</h1>
            <p className="text-gray-400">Enter the 6-digit code from your authenticator app</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-900 rounded-lg p-8 space-y-6">
            {/* 6-digit code inputs */}
            <div className="flex justify-center gap-3">
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  disabled={lockedOut}
                  className="w-12 h-14 text-center text-2xl font-mono bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none disabled:opacity-50"
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 rounded-lg p-3 text-sm text-center">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifying || code.some((d) => d === '') || lockedOut}
              className="w-full bg-purple-500 text-white rounded-lg py-3 font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? 'Verifying...' : 'Verify'}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="w-full text-gray-400 hover:text-white text-sm transition-colors"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default TwoFactorVerifyPage;
